import type { TFunction } from 'i18next';

const LP = 'landingV2';

export const BENEFIT_IDS = ['sync', 'mileage', 'sms', 'invoice', 'msg', 'forfait', 'gift', 'quote'] as const;

const BENEFIT_ROUTES: Record<(typeof BENEFIT_IDS)[number], string | null> = {
  sync: '/fonctionnalites/double-sync-google',
  mileage: '/fonctionnalites/frais-kilometriques',
  sms: '/fonctionnalites/rappels-sms',
  invoice: '/fonctionnalites/paiement-facturation',
  msg: '/fonctionnalites/messagerie',
  forfait: '/fonctionnalites/forfaits-credits',
  gift: '/fonctionnalites/cartes-cadeaux',
  quote: null,
};

const BENEFIT_ICONS: Record<(typeof BENEFIT_IDS)[number], string> = {
  sync: '🔄',
  mileage: '🚗',
  sms: '📱',
  invoice: '📄',
  msg: '💬',
  forfait: '💳',
  gift: '🎁',
  quote: '✍️',
};

const BENEFIT_COLORS: Record<(typeof BENEFIT_IDS)[number], string> = {
  sync: '#A5765C',
  mileage: '#9b59b6',
  sms: '#0ea5e9',
  invoice: '#22c55e',
  msg: '#f59e0b',
  forfait: '#ec4899',
  gift: '#db2777',
  quote: '#6366f1',
};

export type LandingBenefit = {
  id: (typeof BENEFIT_IDS)[number];
  icon: string;
  title: string;
  description: string;
  color: string;
  to: string | null;
};

export function buildLandingBenefits(t: TFunction<'marketing'>): LandingBenefit[] {
  return BENEFIT_IDS.map((id) => ({
    id,
    icon: BENEFIT_ICONS[id],
    title: t(`${LP}.features.items.${id}.title`),
    description: t(`${LP}.features.items.${id}.description`),
    color: BENEFIT_COLORS[id],
    to: BENEFIT_ROUTES[id],
  }));
}

const FAQ_ITEM_KEYS = [
  'free',
  'booking',
  'professionals',
  'payments',
  'premiumCost',
  'cancel',
  'card',
  'trial',
  'upgrade',
] as const;

export function buildLandingFaqItems(t: TFunction<'marketing'>) {
  return FAQ_ITEM_KEYS.map((key) => ({
    question: t(`${LP}.faq.items.${key}.question`),
    answer: t(`${LP}.faq.items.${key}.answer`),
  }));
}

export const PROFESSION_TABS = [
  { id: 'educateur', switcherKey: 'educator', icon: '🐕', color: '#6366f1', to: '/educateur-canin' },
  { id: 'promeneur', switcherKey: 'walker', icon: '🚶', color: '#10b981', to: '/promeneur-canin' },
  { id: 'sitter', switcherKey: 'sitter', icon: '🏠', color: '#f59e0b', to: '/pet-sitter' },
  { id: 'pension', switcherKey: 'boarding', icon: '🏨', color: '#ec4899', to: '/pension-canine' },
  { id: 'comportementaliste', switcherKey: 'behaviorist', icon: '🧠', color: '#8b5cf6', to: '/comportementaliste' },
  { id: 'autres', switcherKey: 'others', icon: '✨', color: '#A5765C', to: null },
] as const;

export type ProfessionTab = (typeof PROFESSION_TABS)[number];

export function buildProfessionTabs(t: TFunction<'marketing'>) {
  return PROFESSION_TABS.map((tab) => ({
    ...tab,
    title: t(`${LP}.professions.switcher.${tab.switcherKey}.title`),
    description: t(`${LP}.professions.switcher.${tab.switcherKey}.description`),
  }));
}

export function getLandingSchemaFeatures(t: TFunction<'marketing'>): string[] {
  const features = t(`${LP}.schema.features`, { returnObjects: true });
  return Array.isArray(features) ? (features as string[]) : [];
}
